import { createContext, useContext, type ReactNode } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export interface ChatFileCitation {
  index: number;
  endIndex?: number;
  filename: string;
  title: string;
}

const CitationContext = createContext<{
  citations: ChatFileCitation[];
  streaming: boolean;
}>({ citations: [], streaming: false });

export function parseFileCitations(value: unknown): ChatFileCitation[] {
  return Array.isArray(value)
    ? value.filter(
        (source): source is ChatFileCitation =>
          source !== null &&
          typeof source === "object" &&
          Number.isSafeInteger(source.index) &&
          source.index >= 0 &&
          (source.endIndex === undefined ||
            (Number.isSafeInteger(source.endIndex) &&
              source.endIndex >= source.index)) &&
          typeof source.filename === "string" &&
          source.filename.trim().length > 0 &&
          typeof source.title === "string" &&
          source.title.trim().length > 0,
      )
    : [];
}

export function ChatCitationsProvider({
  citations,
  streaming = false,
  children,
}: {
  citations?: unknown;
  streaming?: boolean;
  children: ReactNode;
}) {
  return (
    <CitationContext.Provider
      value={{ citations: parseFileCitations(citations), streaming }}
    >
      {children}
    </CitationContext.Provider>
  );
}

export function useCitedText(text: string) {
  const { citations } = useContext(CitationContext);
  // Provider annotation offsets count Unicode characters, not UTF-16 units.
  const characters = Array.from(text);
  const positions = new Map<number, number[]>();
  citations.forEach((source, number) => {
    if ((source.endIndex ?? source.index) <= characters.length) {
      positions.set(source.index, [
        ...(positions.get(source.index) ?? []),
        number,
      ]);
    }
  });
  let result = "";
  let cursor = 0;
  for (const [index, numbers] of [...positions.entries()].sort(
    ([a], [b]) => a - b,
  )) {
    if (index < cursor) continue;
    result += characters.slice(cursor, index).join("");
    result += numbers
      .map((number) => `[Source](#lawworks-source-${number})`)
      .join("");
    cursor = Math.max(
      index,
      ...numbers.map((number) => citations[number].endIndex ?? index),
    );
  }
  return result + characters.slice(cursor).join("");
}

export function ChatCitation({ number }: { number?: number }) {
  const { citations, streaming } = useContext(CitationContext);
  const source = number === undefined ? undefined : citations[number];
  if (!source || number === undefined) {
    return (
      <span
        data-chat-citation
        className="bg-muted text-muted-foreground mx-0.5 rounded px-1.5 py-0.5 text-xs"
      >
        {streaming ? "Source loading…" : "Source"}
      </span>
    );
  }
  const title =
    source.title === source.filename
      ? source.filename
          .replace(/^\d+-/, "")
          .replace(/\.md$/i, "")
          .replace(/-/g, " ")
          .replace(/^./, (letter) => letter.toUpperCase())
      : source.title;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-chat-citation
            tabIndex={0}
            aria-label={`Source ${number + 1}: ${title}`}
            className="bg-muted text-foreground focus-visible:ring-ring mx-0.5 inline-flex min-w-5 cursor-default items-center justify-center rounded px-1.5 py-0.5 align-baseline text-xs font-medium focus-visible:ring-2"
          >
            {number + 1}
          </span>
        </TooltipTrigger>
        <TooltipContent>{title}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
